/**
 * The POS register's cart changes from the catalog: product clicks, options, scans and quick-adds.
 *
 * The API refuses a cart change that overlaps another, so changes save one at a time, on the cart's
 * line (cart-writer) after any other cart save, and adds of the same line that pile up meanwhile
 * save together. A waiting write on the line can't change, so the merging happens here. Until an add
 * saves, the page shows it on top of the saved cart (`withPendingCatalogAdds`). It leaves the
 * pending list only once its response is in the cache, so a line never jumps back or counts twice.
 */
import queryClient from '@/lib/queryClient'
import {
  getPosCartQuantityByVariantId,
  getPosInventoryErrorMessage,
  getPosVariantUnitPrice,
  getRemainingVariantInventory,
  hasMatchingPosCartItem,
  isPosVariantOutOfStock,
  type PosQuantityByVariantId,
} from '@/lib/pos/inventory'
import {
  addPosCartLine,
  cartMutationKeys,
  cartQueryKeys,
  isCartOrderEnding,
  setCartChangesQueued,
  setPosCartLineQuantity,
  waitForCartSaves,
} from '@/lib/services/carts/carts.query'
import {
  nextTempLineId,
  queueCartWrite,
  queuedCartWrites,
  useShownCartWrites,
  withQueuedCartWrites,
} from '@/lib/services/carts/cart-writer'
import type { CreatedCart, PosCart, PosCartItem } from '@/lib/services/carts/carts.types'
import type { LineItemProduct } from '@/lib/services/line-items/line-items.types'
import { productQueries } from '@/lib/services/products/products.query'
import type {
  Product,
  ProductIndexFilters,
  ProductShowFilters,
  Variant,
} from '@/lib/services/products/products.types'
import { useSyncExternalStore } from 'react'
import { toast } from 'sonner'

/** What the POS catalog loads: products with their options, without add-on-only products. */
export const posCatalogProductFilters = {
  include: ['variants', 'category', 'collections', 'media', 'addOnSets'],
  exclude_add_on_only: true,
} satisfies ProductIndexFilters & ProductShowFilters

const PRICING_REFRESH_ERROR_MESSAGE = 'Unable to refresh current item pricing. Please try again.'

/** The catalog option fields an add reads, and shows on a new line. */
export type CatalogVariant = Pick<
  Variant,
  | 'id'
  | 'name'
  | 'price'
  | 'effective_price'
  | 'active_price_adjustment'
  | 'media'
  | 'use_inventory'
  | 'on_hand'
  | 'in_stock'
>

/** The catalog product fields an add reads, and shows on a new line. */
export type CatalogProduct = Pick<Product, 'id' | 'name'> & LineItemProduct

export interface CatalogAdd {
  organizationId: number
  /** The cart on screen, or null before the page has one. */
  cartId: number | null
  /** Gets the cart the page makes when it has none: the page's own maker, through `ensureCart`. */
  createCart: () => Promise<CreatedCart>
  product: CatalogProduct
  variant: CatalogVariant
  quantity: number
  /** Set for a subscription package, which always gets its own line. */
  subscriptionFrequency: string | null
  /** True when the option was just loaded, as a scan's lookup does, so its price isn't re-checked. */
  isVariantCurrent: boolean
  /** Called once the add saves, with the quantity saved (less than asked when stock ran short). */
  onSaved?: (quantity: number) => void
  /** Called when the add doesn't save. */
  onFailed?: () => void
}

/** An add on screen that hasn't saved yet. */
export interface PendingCatalogAdd {
  id: number
  organizationId: number
  /** Null until the page's first cart is made. */
  cartId: number | null
  product: CatalogProduct
  variant: CatalogVariant
  quantity: number
  subscriptionFrequency: string | null
  isVariantCurrent: boolean
  createCart: () => Promise<CreatedCart>
  callbacks: ReadonlyArray<Pick<CatalogAdd, 'onSaved' | 'onFailed'>>
  /** Sent to the API; later adds of its line wait for the next save. */
  isSaving: boolean
  /** The id the cart shows its pending line under, from the cart writer's shared counter. */
  lineId: number
}

export type CatalogAddResult = { status: 'queued'; quantity: number } | { status: 'out-of-stock' }

export interface PosCartActions {
  /** Shows the add at once and saves it after the changes ahead of it. */
  addCatalogItem: (add: CatalogAdd) => CatalogAddResult
  /**
   * The cart this page made while it had none, made once however many controls ask at once. Every
   * control that makes the page's cart goes through it.
   */
  ensureCart: (
    organizationId: number,
    createCart: () => Promise<CreatedCart>
  ) => Promise<CreatedCart>
  /** Forgets the cart this page made, and drops adds not sent yet, when the florist leaves it. */
  leaveCart: () => void
  /** Counts the cart as busy until `work` settles, for a lookup that ends in an add. */
  track: <T>(work: Promise<T>) => Promise<T>
  subscribe: (listener: () => void) => () => void
  getPendingAdds: () => readonly PendingCatalogAdd[]
}

/** How a pending add shows as a new line, under a temporary id no saved or other pending line has. */
function buildPendingCartItem(add: PendingCatalogAdd, cartId: number): PosCartItem {
  const { variant, product } = add

  return {
    id: add.lineId,
    cart_id: cartId,
    variant_id: variant.id,
    quantity: add.quantity,
    price: getPosVariantUnitPrice(variant),
    subscription_frequency: add.subscriptionFrequency,
    variant: {
      id: variant.id,
      name: variant.name,
      price: variant.price,
      effective_price: variant.effective_price ?? variant.price,
      active_price_adjustment: variant.active_price_adjustment ?? null,
      media: variant.media ?? null,
      product,
      use_inventory: variant.use_inventory,
      on_hand: variant.on_hand ?? null,
      in_stock: variant.in_stock,
    },
  }
}

/** The lines of a cart the POS shows, which its adds that haven't saved yet go on top of. */
type PosCartLines = Pick<PosCart, 'id' | 'cart_items'>

/** The cart as the florist should see it: the saved cart plus its adds that haven't saved yet. */
export function withPendingCatalogAdds<TCart extends PosCartLines>(
  cart: TCart,
  pendingAdds: readonly PendingCatalogAdd[]
): TCart {
  const cartId = cart.id
  const cartAdds = pendingAdds.filter((add) => add.cartId === cartId)

  if (cartAdds.length === 0) {
    return cart
  }

  const cartItems = [...cart.cart_items]

  for (const add of cartAdds) {
    const lineIndex =
      add.subscriptionFrequency === null
        ? cartItems.findIndex((item) => hasMatchingPosCartItem(item, add.variant))
        : -1
    const line = cartItems[lineIndex]

    if (line) {
      cartItems[lineIndex] = { ...line, quantity: line.quantity + add.quantity }
    } else {
      cartItems.push(buildPendingCartItem(add, cartId))
    }
  }

  return { ...cart, cart_items: cartItems }
}

function getSavedCart(organizationId: number, cartId: number): PosCart | undefined {
  return queryClient.getQueryData<PosCart>(cartQueryKeys.posShow({ organizationId, cartId }))
}

async function fetchCurrentVariant({
  organizationId,
  product,
  variant,
}: PendingCatalogAdd): Promise<CatalogVariant> {
  const currentProduct = await queryClient.fetchQuery({
    ...productQueries.show({
      organizationId,
      productId: product.id,
      filters: posCatalogProductFilters,
    }),
    staleTime: 0,
  })
  const currentVariant = currentProduct.variants?.find((candidate) => candidate.id === variant.id)

  if (!currentVariant) {
    throw new Error(PRICING_REFRESH_ERROR_MESSAGE)
  }

  return currentVariant
}

export function createPosCartActions(): PosCartActions {
  // Identifies this page's queue in the shared busy state.
  const owner = {}
  const listeners = new Set<() => void>()
  let pendingAdds: readonly PendingCatalogAdd[] = []
  let nextAddId = 1
  let trackedCount = 0
  let isSaving = false
  let createdCart: { organizationId: number; cart: CreatedCart } | null = null
  let cartCreation: { organizationId: number; promise: Promise<CreatedCart> } | null = null

  const updateQueued = () => {
    setCartChangesQueued(owner, pendingAdds.length > 0 || trackedCount > 0)
  }

  const setPendingAdds = (nextPendingAdds: readonly PendingCatalogAdd[]) => {
    pendingAdds = nextPendingAdds
    updateQueued()
    listeners.forEach((listener) => listener())
  }

  const getCreatedCartId = (organizationId: number) =>
    createdCart?.organizationId === organizationId ? createdCart.cart.id : null

  // What the florist sees in the cart, by option: the saved cart, the line changes still on their
  // way to it, and its pending adds.
  const getShownQuantities = (
    organizationId: number,
    cartId: number | null
  ): PosQuantityByVariantId => {
    const savedCart = cartId === null ? undefined : getSavedCart(organizationId, cartId)
    const quantities = getPosCartQuantityByVariantId(
      savedCart ? withQueuedCartWrites(savedCart, queuedCartWrites()).cart_items : [],
      savedCart?.order_change?.held_units
    )

    for (const add of pendingAdds) {
      if (add.organizationId === organizationId && add.cartId === cartId) {
        const key = String(add.variant.id)
        quantities[key] = (quantities[key] ?? 0) + add.quantity
      }
    }

    return quantities
  }

  const ensureCart: PosCartActions['ensureCart'] = (organizationId, createCart) => {
    if (createdCart?.organizationId === organizationId) {
      return Promise.resolve(createdCart.cart)
    }

    if (cartCreation?.organizationId === organizationId) {
      return cartCreation.promise
    }

    const promise = createCart().then((cart) => {
      // A cart the florist left while it was being made isn't theirs to add to.
      if (cartCreation?.promise === promise) {
        createdCart = { organizationId, cart }
        cartCreation = null
        // Adds made before the page had a cart belong to it.
        setPendingAdds(
          pendingAdds.map((add) =>
            add.cartId === null && add.organizationId === organizationId
              ? { ...add, cartId: cart.id }
              : add
          )
        )
      }

      return cart
    })
    cartCreation = { organizationId, promise }
    promise.catch(() => {
      if (cartCreation?.promise === promise) {
        cartCreation = null
      }
    })

    return promise
  }

  const notifySaved = (add: PendingCatalogAdd, quantity: number) => {
    add.callbacks.forEach(({ onSaved }) => onSaved?.(quantity))
  }

  const notifyFailed = (add: PendingCatalogAdd) => {
    add.callbacks.forEach(({ onFailed }) => onFailed?.())
  }

  const saveAdd = async (add: PendingCatalogAdd) => {
    const { organizationId, product } = add
    let isLineUpdate = false

    try {
      const cartId = add.cartId ?? (await add.createCart()).id
      // The add takes its turn on the cart's line, so it starts from the cart every change made
      // before it saved; the catalog's own pending list shows it meanwhile.
      const saved = await queueCartWrite(organizationId, cartId, {
        mutationKey: cartMutationKeys.addItem,
        send: async () => {
          const variant = add.isVariantCurrent ? add.variant : await fetchCurrentVariant(add)
          const savedCart = getSavedCart(organizationId, cartId)
          // An order change may reuse the stock its order already holds.
          const savedQuantities = getPosCartQuantityByVariantId(
            savedCart?.cart_items,
            savedCart?.order_change?.held_units
          )

          if (isPosVariantOutOfStock(variant, savedQuantities)) {
            toast.warning(`${product.name} is out of stock.`)
            return null
          }

          // The API refuses a change past the stock left, so the add saves only what's left.
          const remaining = getRemainingVariantInventory(variant, savedQuantities)
          const quantity = remaining === null ? add.quantity : Math.min(add.quantity, remaining)

          if (quantity < add.quantity) {
            toast.warning(`Only ${remaining} of ${product.name} left in stock.`)
          }

          const line =
            add.subscriptionFrequency === null
              ? savedCart?.cart_items.find((item) => hasMatchingPosCartItem(item, variant))
              : undefined

          if (line) {
            isLineUpdate = true
            await setPosCartLineQuantity(organizationId, {
              cartId,
              cartItemId: line.id,
              quantity: line.quantity + quantity,
            })
          } else {
            await addPosCartLine(organizationId, {
              cartId,
              variantId: variant.id,
              quantity,
              subscriptionFrequency: add.subscriptionFrequency,
            })
          }

          return { quantity }
        },
      })

      if (saved) {
        notifySaved(add, saved.quantity)
      } else {
        notifyFailed(add)
      }
    } catch (error) {
      toast.error(isLineUpdate ? 'Failed to update item' : 'Failed to add item', {
        description: getPosInventoryErrorMessage(error) || 'Please try again',
      })
      notifyFailed(add)
    }
  }

  // Normally unreachable: paying, finalizing and clearing wait while adds are queued.
  const dropAddsForEndingOrder = () => {
    const names = [...new Set(pendingAdds.map(({ product }) => product.name))]
    const [firstName] = names

    toast.warning(
      names.length === 1 && firstName
        ? `We didn't add ${firstName}. Add it again once this order finishes.`
        : `We didn't add ${names.length} items. Add them again once this order finishes.`
    )
    setPendingAdds([])
  }

  const saveQueuedAdds = async () => {
    if (isSaving) {
      return
    }

    isSaving = true

    try {
      while (pendingAdds.length > 0) {
        await waitForCartSaves()
        const [next] = pendingAdds

        if (!next) {
          break
        }

        if (isCartOrderEnding()) {
          dropAddsForEndingOrder()
          break
        }

        const saving = { ...next, isSaving: true }
        setPendingAdds(pendingAdds.map((add) => (add.id === saving.id ? saving : add)))
        await saveAdd(saving)
        setPendingAdds(pendingAdds.filter((add) => add.id !== saving.id))
      }
    } finally {
      isSaving = false
    }
  }

  const addCatalogItem: PosCartActions['addCatalogItem'] = (add) => {
    const { organizationId, variant } = add
    const cartId = add.cartId ?? getCreatedCartId(organizationId)
    const shownQuantities = getShownQuantities(organizationId, cartId)

    if (isPosVariantOutOfStock(variant, shownQuantities)) {
      return { status: 'out-of-stock' }
    }

    const remaining = getRemainingVariantInventory(variant, shownQuantities)
    const quantity = remaining === null ? add.quantity : Math.min(add.quantity, remaining)
    const callbacks = { onSaved: add.onSaved, onFailed: add.onFailed }
    const queuedLine =
      add.subscriptionFrequency === null
        ? pendingAdds.find(
            (pending) =>
              !pending.isSaving &&
              pending.organizationId === organizationId &&
              pending.cartId === cartId &&
              pending.subscriptionFrequency === null &&
              hasMatchingPosCartItem(buildPendingCartItem(pending, 0), variant)
          )
        : undefined

    setPendingAdds(
      queuedLine
        ? pendingAdds.map((pending) =>
            pending === queuedLine
              ? {
                  ...pending,
                  quantity: pending.quantity + quantity,
                  callbacks: [...pending.callbacks, callbacks],
                }
              : pending
          )
        : [
            ...pendingAdds,
            {
              id: nextAddId++,
              lineId: nextTempLineId(),
              organizationId,
              cartId,
              product: add.product,
              variant,
              quantity,
              subscriptionFrequency: add.subscriptionFrequency,
              isVariantCurrent: add.isVariantCurrent,
              createCart: add.createCart,
              callbacks: [callbacks],
              isSaving: false,
            },
          ]
    )
    void saveQueuedAdds()

    return { status: 'queued', quantity }
  }

  const leaveCart = () => {
    createdCart = null
    cartCreation = null
    setPendingAdds(pendingAdds.filter((add) => add.isSaving))
  }

  const track: PosCartActions['track'] = async (work) => {
    trackedCount += 1
    updateQueued()

    try {
      return await work
    } finally {
      trackedCount -= 1
      updateQueued()
    }
  }

  return {
    addCatalogItem,
    ensureCart,
    leaveCart,
    track,
    subscribe: (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    getPendingAdds: () => pendingAdds,
  }
}

/**
 * The page's cart as the florist should see it: the saved cart, the line changes still on their way
 * to it, and its catalog adds that haven't saved yet.
 */
export function usePosCartWithPendingAdds(
  cartActions: PosCartActions,
  cart: PosCart | null
): PosCart | null {
  const pendingAdds = useSyncExternalStore(cartActions.subscribe, cartActions.getPendingAdds)
  const queuedWrites = useShownCartWrites()

  if (!cart) {
    return null
  }

  // A queued change comes off as soon as its answer is in the cache, which can be a render before
  // the page's cart query passes that answer down, so the saved cart comes from the cache. Otherwise
  // the line would show its old value for a render.
  const savedCart = getSavedCart(cart.organization_id, cart.id) ?? cart

  return withPendingCatalogAdds(withQueuedCartWrites(savedCart, queuedWrites), pendingAdds)
}
